import {
  forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes,
  type SelectHTMLAttributes, type TextareaHTMLAttributes, type ReactNode,
} from 'react';
import clsx from 'clsx';
import { toneFor, type StatusTone } from '@/tokens';
import { humanise } from '@/lib/api';

// ─── Button ──────────────────────────────────────────────────────────────────

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  isLoading?: boolean;
  icon?: ReactNode;
  size?: 'sm' | 'md';
}

/**
 * Lumina buttons: 8px radius, 500 weight, slight letter-spacing. Primary is
 * the orange accent (the loud thing on the page — use once); secondary and
 * ghost resolve in indigo, the platform's interactive colour.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', isLoading, icon, size = 'md', className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md font-sans font-medium',
        'tracking-[0.01em] transition-all duration-fast ease-lumina',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none',
        size === 'sm' ? 'h-8 px-3 text-caption' : 'h-9 px-4 text-body',
        variant === 'primary' &&
          'bg-cta text-ink-inverse shadow-sm hover:bg-cta-deep hover:shadow-md active:bg-cta-deep',
        variant === 'secondary' &&
          'border border-rule-strong bg-surface text-secondary shadow-sm hover:bg-secondary/[0.06] active:bg-secondary/10 dark:border-rule',
        variant === 'ghost' &&
          'text-secondary hover:bg-secondary/[0.06] active:bg-secondary/10',
        variant === 'destructive' &&
          'bg-negative-fill text-ink-inverse shadow-sm hover:opacity-90 active:opacity-85',
        className,
      )}
      {...props}
    >
      {isLoading ? (
        <span
          aria-hidden
          className="h-3 w-3 animate-spin rounded-full border border-current border-t-transparent"
        />
      ) : (
        icon
      )}
      {children}
    </button>
  );
});

// ─── Status badge ────────────────────────────────────────────────────────────

const TONE_CLASS: Record<StatusTone, string> = {
  neutral: 'bg-ink/[0.06] text-ink-secondary dark:bg-ink/10 dark:text-ink',
  info: 'bg-secondary/[0.08] text-secondary dark:bg-secondary/20 dark:text-[#A5B4FC]',
  positive: 'bg-positive/[0.08] text-positive dark:bg-positive/15',
  warning: 'bg-warning/[0.08] text-warning dark:bg-warning/15',
  negative: 'bg-negative/[0.08] text-negative dark:bg-negative/15 dark:text-[#FCA5A5]',
  critical: 'bg-negative/[0.08] text-negative dark:bg-negative/15 dark:text-[#FCA5A5]',
  accent: 'bg-primary/[0.08] text-primary-ink dark:bg-primary/15 dark:text-[#FFA45C]',
};

/**
 * Tinted status pill. `critical` keeps a small orange key-indicator dot — the
 * states that demand attention today (failed QC, overdue trial, sync error).
 */
export function StatusBadge({
  status,
  className,
}: {
  status: string | null | undefined;
  className?: string;
}) {
  const tone = toneFor(status);
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 font-sans text-body-sm font-medium leading-none',
        TONE_CLASS[tone],
        className,
      )}
    >
      {tone === 'critical' && (
        <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
      )}
      {humanise(status)}
    </span>
  );
}

/** Signed delta with a muted colour — never orange (§2). */
export function Delta({ value, suffix = '%' }: { value: number | string | null | undefined; suffix?: string }) {
  if (value === null || value === undefined || value === '') return <span className="text-ink-tertiary">—</span>;
  const n = Number(value);
  if (Number.isNaN(n)) return <span className="text-ink-tertiary">—</span>;
  return (
    <span className={clsx('font-sans tabular', n > 0 ? 'text-positive' : n < 0 ? 'text-negative' : 'text-ink-tertiary')}>
      {n > 0 ? '▲' : n < 0 ? '▼' : ''}
      {Math.abs(n).toFixed(1)}
      {suffix}
    </span>
  );
}

// ─── Form field wrapper ──────────────────────────────────────────────────────

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({ label, hint, error, required, children, className }: FieldProps) {
  return (
    <label className={clsx('block', className)}>
      {label && (
        <span className="mb-1.5 block font-sans text-body-sm font-medium text-ink-secondary">
          {label}
          {required && <span className="ml-1 text-primary-ink">*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="mt-1 block text-caption text-negative">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-caption text-ink-tertiary">{hint}</span>
      ) : null}
    </label>
  );
}

const controlBase =
  'rounded-md border border-rule bg-surface-hover text-ink ' +
  'placeholder:text-ink-tertiary transition-all duration-fast ' +
  'hover:border-rule-strong focus:border-secondary/60 focus:outline-none ' +
  'focus:ring-2 focus:ring-secondary/20 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-active disabled:text-ink-tertiary ' +
  'dark:bg-ink/[0.04]';

/**
 * Padding and type size travel together.
 *
 * A compact control is not a full-size one with the padding taken off: shrink
 * the box without shrinking the text and the text is simply clipped. Offering
 * the pair as one choice is what stops that happening — and it has to live
 * here rather than in a caller's class string, for the reason below.
 */
export type ControlSize = 'sm' | 'md';

// Padding-driven heights (≈36px compact, ≈40px standard) so the same recipe
// fits multiline textareas without a fixed height fighting `rows`.
const CONTROL_SIZE: Record<ControlSize, string> = {
  sm: 'px-2.5 py-[7px] text-caption',
  md: 'px-3 py-[8.5px] text-body',
};

/**
 * Form controls default to full width, but a caller passing an explicit width
 * must win.
 *
 * Tailwind cannot resolve conflicting utilities by their order in a class
 * string — `w-full` and `w-32` have equal specificity, so whichever the
 * stylesheet emits later applies, regardless of what the caller wrote. Rather
 * than pull in tailwind-merge for this one case, the default is simply omitted
 * when the caller supplies a width.
 *
 * The same trap catches padding, which is why `size` is a prop: a caller
 * appending `py-0` to shrink a control gets no padding change at all, because
 * `py-2` is emitted later in the sheet and wins.
 */
function controlClass(className?: string, size: ControlSize = 'md') {
  const hasWidth = className ? /(^|\s)(w-|min-w-|max-w-|flex-1)/.test(className) : false;
  return clsx(controlBase, CONTROL_SIZE[size], !hasWidth && 'w-full', className);
}

export const Input = forwardRef<
  HTMLInputElement,
  // The native `size` attribute is a character count and is never used here;
  // the prop is the control's scale, as it is on Button.
  Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & { size?: ControlSize }
>(function Input({ className, type = 'text', size, ...props }, ref) {
  return (
    <input
      ref={ref}
      type={type}
      className={clsx(
        controlClass(className, size),
        (type === 'number' || type === 'date') && 'tabular font-sans',
      )}
      {...props}
    />
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { size?: ControlSize }
>(function Textarea({ className, rows = 3, size, ...props }, ref) {
  return (
    <textarea ref={ref} rows={rows} className={clsx(controlClass(className, size), 'resize-y')} {...props} />
  );
});

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  size?: ControlSize;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, options, placeholder, size, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      className={clsx(controlClass(className, size), 'appearance-none pr-7')}
      // A filter select often has no label, only a placeholder ("Any
      // priority"). A title names it for screen readers; a real <label>
      // still wins where there is one (VIS-015).
      title={placeholder}
      {...props}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
});

/** Multi-select rendered as a checkbox list — a native multiple is unusable. */
export function MultiSelect({
  options,
  value,
  onChange,
  columns = 1,
}: {
  options: Array<{ value: string; label: string; hint?: string }>;
  value: string[];
  onChange: (next: string[]) => void;
  columns?: number;
}) {
  const toggle = (v: string) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <div className={clsx('grid gap-1', columns === 2 && 'sm:grid-cols-2')}>
      {options.map((o) => (
        <label
          key={o.value}
          className="flex cursor-pointer items-start gap-2 py-1 text-body hover:text-ink"
        >
          <input
            type="checkbox"
            checked={value.includes(o.value)}
            onChange={() => toggle(o.value)}
            className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm border-rule-strong text-secondary focus:ring-secondary/30"
          />
          <span>
            {o.label}
            {o.hint && <span className="ml-1.5 text-caption text-ink-tertiary">{o.hint}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
  ariaLabel,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** The name for a box shown without a visible label (a table cell). */
  ariaLabel?: string;
}) {
  return (
    <label className={clsx('flex cursor-pointer items-center gap-2 text-body', disabled && 'cursor-not-allowed opacity-50')}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label ? undefined : ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 rounded-sm border-rule-strong text-secondary focus:ring-secondary/30"
      />
      {label}
    </label>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={clsx('relative', className)}>
      <svg
        aria-hidden
        viewBox="0 0 16 16"
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary"
      >
        <circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M11 11l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={clsx(controlClass(undefined), 'pl-8')}
      />
      {value && (
        <button
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-tertiary hover:text-ink"
        >
          ×
        </button>
      )}
    </div>
  );
}

export function FileUpload({
  onSelect,
  accept,
  label = 'Choose file',
  isUploading,
  multiple,
}: {
  onSelect: (files: FileList) => void;
  accept?: string;
  label?: string;
  isUploading?: boolean;
  multiple?: boolean;
}) {
  return (
    <label
      className={clsx(
        'flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-rule-strong bg-surface-hover/50',
        'px-4 py-6 text-center transition-colors hover:border-secondary/50 hover:bg-secondary/[0.04]',
        isUploading && 'pointer-events-none opacity-60',
      )}
    >
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        onChange={(e) => e.target.files?.length && onSelect(e.target.files)}
      />
      <span className="label">{isUploading ? 'Uploading…' : label}</span>
    </label>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

/** Centred and quiet: a muted glyph, a line in secondary ink, an optional CTA. */
export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <svg
        aria-hidden
        viewBox="0 0 40 40"
        className="h-10 w-10 text-ink-tertiary/60"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="6" y="10" width="28" height="22" rx="3" />
        <path d="M6 17h28M13 25h8" strokeLinecap="round" />
      </svg>
      <p className="font-sans text-body text-ink-secondary">{message}</p>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={clsx(
        'inline-block h-4 w-4 animate-spin rounded-full border-2 border-ink-tertiary border-t-transparent',
        className,
      )}
    />
  );
}

export function SkeletonRows({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-rule/50">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 py-3">
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} className="skeleton h-3 flex-1" style={{ maxWidth: c === 0 ? '30%' : '18%' }} />
          ))}
        </div>
      ))}
    </div>
  );
}
